import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Users, Award, TrendingUp, Calendar, Target, Briefcase, MessageCircle, ArrowRight } from 'lucide-react';

const StudentAmbassadors = () => {
    const navigate = useNavigate();

    const benefits = [
        {
            icon: <Award className="w-6 h-6" />,
            title: "Free Premium Access",
            description: "Get full access to DataCreator AI for 3 months"
        },
        // {
        //     icon: <Briefcase className="w-6 h-6" />,
        //     title: "Letter of Recommendation",
        //     description: "Performance-based LoR for your future opportunities"
        // },
        {
            icon: <MessageCircle className="w-6 h-6" />,
            title: "Weekly Project Guidance",
            description: "Get help with your AI/ML projects and technical doubts"
        },
        {
            icon: <Users className="w-6 h-6" />,
            title: "Featured on Our Website",
            description: "Your profile and projects showcased to our community"
        },
        {
            icon: <CheckCircle className="w-6 h-6" />,
            title: "Certificate",
            description: "Official certificate of participation in the program"
        },
        {
            icon: <TrendingUp className="w-6 h-6" />,
            title: "10% Commission",
            description: "Earn from every user who takes a paid plan"
        }
    ];

    const responsibilities = [
        "Use DataCreator AI in your AI/ML projects",
        "Attend 1 meetings per month (online)",
        "Share your experience on LinkedIn twice a month",
        "Introduce DataCreator AI to your college network",
        "Provide feedback on features and improvements"
    ];

    const eligibility = [
        "2nd to final year students",
        "Working on AI/ML projects requiring datasets",
        "Understanding of Synthetic Data and its applications",
        "Active on LinkedIn",
        "Good communication skills"
    ];

    return (
        <div className="min-h-screen bg-white">
            {/* Hero Section */}
            <div className="bg-white border-b border-gray-100">
                <div className="container mx-auto px-6 py-16 md:py-24">
                    <div className="max-w-4xl mx-auto text-center">
                        <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-full text-sm font-medium mb-6">
                            <span className="w-2 h-2 bg-blue-600 rounded-full animate-pulse"></span>
                            Applications open until Feb 28, 2026
                        </div>

                        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                            Student Ambassador Program
                        </h1>

                        <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                            Join us in shaping the future of AI. Build your AI portfolio,
                            get mentorship, and earn while you learn.
                        </p>

                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <a
                                href="https://forms.gle/adwyG7GxmVGks7Wi7"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center justify-center px-8 py-4 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors duration-200"
                            >
                                Apply Now
                                <ArrowRight className="w-5 h-5 ml-2" />
                            </a>
                            <button
                                onClick={() => {
                                    const benefitsSection = document.getElementById('benefits');
                                    benefitsSection?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                }}
                                className="inline-flex items-center justify-center px-8 py-4 border-2 border-gray-200 text-gray-700 font-semibold rounded-lg hover:border-gray-300 hover:bg-gray-50 transition-colors duration-200"
                            >
                                Learn More
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Quick Info */}
            <div className="bg-gray-50 border-b border-gray-100">
                <div className="container mx-auto px-6 py-8">
                    <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
                        <div>
                            <div className="flex items-center justify-center gap-2 text-gray-900 font-semibold mb-1">
                                <Calendar className="w-5 h-5 text-blue-600" />
                                Duration
                            </div>
                            <p className="text-gray-600">3 months (extendable)</p>
                        </div>
                        <div>
                            <div className="flex items-center justify-center gap-2 text-gray-900 font-semibold mb-1">
                                <Target className="w-5 h-5 text-blue-600" />
                                Commitment
                            </div>
                            <p className="text-gray-600">1-2 meetings/month</p>
                        </div>
                        <div>
                            <div className="flex items-center justify-center gap-2 text-gray-900 font-semibold mb-1">
                                <Users className="w-5 h-5 text-blue-600" />
                                Eligibility
                            </div>
                            <p className="text-gray-600">2nd to final year students</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* What You'll Get */}
            <div id="benefits" className="py-20">
                <div className="container mx-auto px-6">
                    <div className="max-w-5xl mx-auto">
                        <div className="text-center mb-12">
                            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                                What You'll Get
                            </h2>
                            <p className="text-lg text-gray-600">
                                Benefits designed to help you grow as an AI professional
                            </p>
                        </div>

                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {benefits.map((benefit, index) => (
                                <div
                                    key={index}
                                    className="p-6 border border-gray-200 rounded-lg hover:border-blue-600 hover:shadow-md transition-all duration-200"
                                >
                                    <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-lg flex items-center justify-center mb-4">
                                        {benefit.icon}
                                    </div>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                        {benefit.title}
                                    </h3>
                                    <p className="text-gray-600 text-sm">
                                        {benefit.description}
                                    </p>
                                </div>
                            ))}
                        </div>

                        <div className="mt-8 p-6 bg-blue-50 border border-blue-100 rounded-lg">
                            <p className="text-gray-700">
                                <strong>Additional Benefits:</strong> Ambassadors who actively contribute to the community and help introduce DataCreator AI to relevant users may receive additional recognition such as Letters of Recommendation and program extensions. Quality referrals are prioritized over volume.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Your Role */}
            <div className="py-20 bg-gray-50">
                <div className="container mx-auto px-6">
                    <div className="max-w-4xl mx-auto">
                        <div className="text-center mb-12">
                            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                                Your Role
                            </h2>
                            <p className="text-lg text-gray-600">
                                Flexible responsibilities that fit your schedule
                            </p>
                        </div>

                        <div className="bg-white border border-gray-200 rounded-lg p-8">
                            <ul className="space-y-4">
                                {responsibilities.map((responsibility, index) => (
                                    <li key={index} className="flex items-start gap-3">
                                        <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                                        <span className="text-gray-700">{responsibility}</span>
                                    </li>
                                ))}
                            </ul>

                            <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                                <p className="text-sm text-gray-600">
                                    <strong className="text-gray-900">Flexibility:</strong> You may leave anytime with one week’s notice. Certificates are provided only upon successful completion of the 3-month program.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Who We're Looking For */}
            <div className="py-20">
                <div className="container mx-auto px-6">
                    <div className="max-w-4xl mx-auto">
                        <div className="text-center mb-12">
                            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                                Who We're Looking For
                            </h2>
                            <p className="text-lg text-gray-600">
                                Are you the right fit?
                            </p>
                        </div>

                        <div className="grid md:grid-cols-2 gap-4 mb-8">
                            {eligibility.map((criterion, index) => (
                                <div
                                    key={index}
                                    className="flex items-center gap-3 p-4 bg-white border border-gray-200 rounded-lg"
                                >
                                    <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold">
                                        ✓
                                    </div>
                                    <span className="text-gray-700">{criterion}</span>
                                </div>
                            ))}
                        </div>

                        <div className="p-6 bg-blue-50 border border-blue-100 rounded-lg">
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                Qualifying Projects
                            </h3>
                            <p className="text-gray-700 mb-3">
                                Any AI/ML projects that require text datasets qualify! This includes AI Agents, NLP, chatbots,
                                text classification, sentiment analysis, instruction fine-tuning, and more.
                            </p>
                            <p className="text-gray-600 text-sm mb-3">
                                <strong>Note:</strong> Currently, we support text data generation. Audio data support is coming soon!
                            </p>
                            <div className="mt-4 p-3 bg-white border border-blue-200 rounded">
                                <p className="text-gray-700 text-sm">
                                    <strong className="text-blue-600">Important:</strong> DataCreator AI generates <strong>synthetic data</strong> using AI models, not web scraping.
                                    We create privacy-safe, customizable datasets for training and testing AI systems, serving a completely different purpose than web scraping tools.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* How It Works */}
            <div className="py-20 bg-gray-50">
                <div className="container mx-auto px-6">
                    <div className="max-w-5xl mx-auto">
                        <div className="text-center mb-12">
                            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                                How It Works
                            </h2>
                            <p className="text-lg text-gray-600">
                                Simple steps to join the program
                            </p>
                        </div>

                        <div className="grid md:grid-cols-4 gap-8">
                            {[
                                { step: "1", title: "Apply", description: "Submit your application by Feb 28, 2026" },
                                { step: "2", title: "Review", description: "We'll review your profile and project" },
                                { step: "3", title: "Interview", description: "Quick call to understand you better" },
                                { step: "4", title: "Start", description: "Begin your 3-month journey" }
                            ].map((item, index) => (
                                <div key={index} className="text-center">
                                    <div className="w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                                        {item.step}
                                    </div>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                        {item.title}
                                    </h3>
                                    <p className="text-gray-600 text-sm">
                                        {item.description}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* FAQ */}
            <div className="py-20">
                <div className="container mx-auto px-6">
                    <div className="max-w-4xl mx-auto">
                        <div className="text-center mb-12">
                            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                                Frequently Asked Questions
                            </h2>
                        </div>

                        <div className="space-y-6">
                            {[
                                {
                                    q: "How much time commitment is required?",
                                    a: "1-2 meetings per month (1-2 hours total) and at least two LinkedIn posts per month. The rest depends on how actively you want to promote DataCreator AI."
                                },
                                {
                                    q: "How does the commission work?",
                                    a: "You'll earn 10% commission on any referrals that convert to paid subscriptions. Commission is paid at the end of each month. We'll provide you with a unique referral code."
                                },
                                {
                                    q: "Can I publish my projects publicly?",
                                    a: "Absolutely! We encourage you to publish your projects. We'll also feature your projects on our website."
                                },
                                {
                                    q: "What happens after 3 months?",
                                    a: "Based on your performance and engagement, we may extend the program. High-performing ambassadors get priority for extensions."
                                },
                                {
                                    q: "Can international students apply?",
                                    a: "Yes! We welcome applications from students worldwide. As long as you meet the eligibility criteria and can attend online meetings, you're welcome to apply."
                                }
                            ].map((faq, index) => (
                                <div key={index} className="p-6 bg-white border border-gray-200 rounded-lg">
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                        {faq.q}
                                    </h3>
                                    <p className="text-gray-600">
                                        {faq.a}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* CTA */}
            <div className="py-20 bg-blue-600">
                <div className="container mx-auto px-6 text-center">
                    <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                        Ready to Join?
                    </h2>
                    <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
                        Applications close on February 28, 2026. Don't miss this opportunity to grow your AI career.
                    </p>
                    <a
                        href="https://forms.gle/adwyG7GxmVGks7Wi7"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center px-8 py-4 bg-white text-blue-600 font-semibold rounded-lg hover:bg-blue-50 transition-colors duration-200"
                    >
                        Apply Now
                        <ArrowRight className="w-5 h-5 ml-2" />
                    </a>
                    <p className="mt-6 text-sm text-blue-100">
                        This is not a paid position or internship. This is a performance-based opportunity.
                    </p>
                    <p className="mt-3 text-xs text-blue-200 max-w-3xl mx-auto">
                        Commission payments are subject to applicable tax laws. Ambassadors are responsible for reporting income in their tax returns.
                        DataCreator AI reserves the right to modify or terminate the program at any time with reasonable notice.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default StudentAmbassadors;
